import type { FC, PropsWithChildren, ReactNode } from "react";
import { Box, Column } from "@hightouch/htui";

import { PAPER_RIGHT_PADDING_PX } from "src/components/paper";
import { useHeaderHeight } from "src/contexts/header-height-context";

/** A panel beside a docked agent sidebar, such as a focused chat message opened for review. */
export type SidebarPanel = {
  isOpen: boolean;
  /** The open sidebar's width, so the panel takes the rest of the row. */
  sidebarWidthPx: number;
  /** Where the host renders or portals its panel content. */
  ref: (node: HTMLDivElement | null) => void;
  label: string;
};

/**
 * Page card, sidebar, and panel side by side in a row twice the viewport
 * wide. Opening slides the row left so the sidebar and panel fill the view.
 * Column widths stay fixed so the page card never reflows per frame.
 */
export const SidebarPanelRow: FC<
  PropsWithChildren<{
    panel: SidebarPanel;
    sidebar: ReactNode;
    isWindowScroll: boolean;
  }>
> = ({ panel, sidebar, isWindowScroll, children }) => {
  const { headerHeight } = useHeaderHeight();
  const sidebarWidth = `${panel.sidebarWidthPx}px`;

  return (
    // clip rather than hidden: a scroll container would stop the sidebar sticking.
    <Box
      minWidth={0}
      minHeight={isWindowScroll ? undefined : 0}
      overflowX="clip"
    >
      <Box
        display="grid"
        // The row's height, which the page card holds while it scrolls its own content.
        height={isWindowScroll ? undefined : "100%"}
        // One row no taller than that, so the card shrinks to it and scrolls rather than growing with its content.
        gridTemplateRows={isWindowScroll ? undefined : "minmax(0, 1fr)"}
        width={`calc(200% - ${sidebarWidth})`}
        gridTemplateColumns={`minmax(0, 1fr) auto calc((100% - ${sidebarWidth}) / 2)`}
        // No transform while closed: it would make the overlaid sidebar's fixed positioning relative to this row.
        transform={
          panel.isOpen
            ? `translateX(calc((${sidebarWidth} - 100%) / 2))`
            : undefined
        }
        transitionProperty="transform"
        transitionDuration="normal"
        transitionTimingFunction="easeOutCubic"
        _mediaReduceMotion={{ transitionDuration: "0s" }}
      >
        <Column
          minWidth={0}
          minHeight={isWindowScroll ? undefined : 0}
          visibility={panel.isOpen ? "hidden" : "visible"}
          transitionProperty="visibility"
          transitionDuration="normal"
          _mediaReduceMotion={{ transitionDuration: "0s" }}
          aria-hidden={panel.isOpen || undefined}
        >
          {children}
        </Column>
        {sidebar}
        <Column
          as="section"
          aria-label={panel.label}
          position="sticky"
          top={0}
          alignSelf="start"
          height={isWindowScroll ? `calc(100vh - ${headerHeight}px)` : "100%"}
          minWidth={0}
          pt={4}
          pr={`${PAPER_RIGHT_PADDING_PX}px`}
          visibility={panel.isOpen ? "visible" : "hidden"}
          transitionProperty="visibility"
          transitionDuration="normal"
          _mediaReduceMotion={{ transitionDuration: "0s" }}
          aria-hidden={!panel.isOpen || undefined}
        >
          <Column
            ref={panel.ref}
            flex={1}
            minHeight={0}
            bg="base.white"
            borderWidth="hairline"
            borderColor="base.border"
            borderTopRadius="xl"
            overflow="hidden"
          />
        </Column>
      </Box>
    </Box>
  );
};
